(() => {
  if (window.diPexMotionReady) return;
  window.diPexMotionReady = true;
  const instances = new Map();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const steps = ['record', 'test', 'learn'];
  const createArt = (root, image, canAnimate) => {
    const names = ['record', 'test', 'learn'];
    const urls = names.map(name => root.dataset[`pex${name[0].toUpperCase()}${name.slice(1)}Src`]);
    if (!urls.every(Boolean)) return null;
    const cache = urls.map(src => ({ src, state: 'loading', loader: new Image() }));
    let selected = 0;
    let displayed = image.getAttribute('src') || image.src;
    let desired = displayed;
    let opacity = 1;
    let frame = 0;
    let last = 0;
    let disposed = false;
    root.dataset.pexDisplayed = names[urls.indexOf(displayed)] || 'fallback';
    const paint = () => { image.style.opacity = String(opacity); };
    const swap = () => {
      if (displayed === desired) return;
      displayed = desired;
      image.src = displayed;
      root.dataset.pexDisplayed = names[urls.indexOf(displayed)] || 'fallback';
    };
    const stop = () => { cancelAnimationFrame(frame); frame = 0; last = 0; };
    const settle = () => { stop(); swap(); opacity = 1; paint(); };
    const recover = () => {
      const failed = cache.find(item => item.src === displayed);
      if (failed) failed.state = 'failed';
      if (root.dataset.pexFallbackSrc && displayed !== root.dataset.pexFallbackSrc) {
        desired = root.dataset.pexFallbackSrc;
        settle();
      }
    };
    const tick = time => {
      frame = 0;
      if (disposed) return;
      if (!canAnimate()) { settle(); return; }
      const dt = Math.min(40, Math.max(0, time - (last || time - 16.667)));
      last = time;
      if (displayed !== desired) {
        opacity = Math.max(0, opacity - dt / 150);
        if (opacity === 0) { paint(); swap(); }
      } else opacity = Math.min(1, opacity + dt / 180);
      paint();
      if (displayed !== desired || opacity < 1) frame = requestAnimationFrame(tick);
      else last = 0;
    };
    const update = (index, animate = true) => {
      if (disposed) return;
      selected = index;
      const target = cache[selected];
      desired = target?.state === 'ready' ? target.src : displayed;
      if (!animate || !canAnimate()) { settle(); return; }
      if ((desired !== displayed || opacity < 1) && !frame) frame = requestAnimationFrame(tick);
    };
    cache.forEach(item => {
      const loaded = async () => {
        try {
          if (typeof item.loader.decode === 'function') await item.loader.decode();
          if (disposed || item.state !== 'loading') return;
          if (!item.loader.naturalWidth) throw new Error('empty image');
          item.state = 'ready';
          update(selected);
        } catch { item.state = 'failed'; update(selected); }
      };
      item.loader.onload = loaded;
      item.loader.onerror = () => { item.state = 'failed'; if (displayed === item.src) recover(); update(selected); };
      item.loader.src = item.src;
    });
    return {
      update,
      recover,
      settle: () => update(selected, false),
      dispose: () => { disposed = true; stop(); cache.forEach(item => { item.loader.onload = null; item.loader.onerror = null; }); opacity = 1; paint(); }
    };
  };

  const poses = [
    { x: -0.7, y: 0, rotation: -3 },
    { x: 1.1, y: 0, rotation: 4 },
    { x: 0, y: -1.4, rotation: 0 }
  ];
  const neutral = { x: 0, y: 0, rotation: 0 };
  const keys = Object.keys(neutral);

  function init(root) {
    if (instances.has(root) || !root.closest('[data-di-notepad]')) return;
    const image = root.querySelector('[data-di-pex-image]');
    const figure = root.querySelector('.di-pex__figure');
    const hello = root.querySelector('[data-di-pex-hello]');
    const pause = root.querySelector('[data-di-pex-pause]');
    const celebrate = root.querySelector('[data-di-pex-celebrate]');
    const caption = root.querySelector('[data-di-pex-caption]');
    const hint = root.querySelector('[data-di-pex-hint]');
    const tabRoot = root.closest('[data-di-tabs]');
    const tabs = tabRoot ? Array.from(tabRoot.querySelectorAll('[data-di-tab]')) : [];
    if (!image || !figure || !hello || !pause || !celebrate || !caption) return;
    const cleanup = [];
    let visible = !('IntersectionObserver' in window);
    let destroyed = false;
    let ready = image.complete && image.naturalWidth > 0;
    let paused = false;
    try { paused = sessionStorage.getItem('di-pex-paused') === '1'; } catch {}
    let step = 0;
    let frame = 0;
    let lastTime = 0;
    let gesture = null;
    const position = { ...neutral };
    const velocity = { ...neutral };
    const listen = (node, type, fn, options) => {
      node.addEventListener(type, fn, options);
      cleanup.push(() => node.removeEventListener(type, fn, options));
    };
    const allowed = () => ready && visible && !destroyed && !document.hidden && !paused && !reduced.matches;
    const art = createArt(root, image, allowed);
    if (art) cleanup.push(() => art.dispose());
    const base = () => art || paused || reduced.matches ? neutral : poses[step];
    const render = () => {
      figure.style.transform = `translate3d(${position.x.toFixed(4)}%, ${position.y.toFixed(4)}%, 0) rotate(${position.rotation.toFixed(4)}deg)`;
    };
    const settle = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      lastTime = 0;
      gesture = null;
      Object.assign(position, base());
      Object.assign(velocity, neutral);
      root.classList.remove('is-playing');
      root.dataset.motion = 'still';
      if (!allowed()) art?.settle();
      render();
    };
    const tick = time => {
      frame = 0;
      if (!allowed()) { settle(); return; }
      const dt = Math.min(0.04, Math.max(0.001, (time - (lastTime || time - 16.667)) / 1000));
      lastTime = time;
      const target = { ...base() };
      if (gesture) {
        if (gesture.start === null) gesture.start = time;
        const progress = Math.min(1, (time - gesture.start) / gesture.duration);
        const envelope = Math.sin(Math.PI * progress) ** 2;
        target.rotation += Math.sin(progress * Math.PI * 4) * envelope * (gesture.kind === 'hello' ? 9 : 5);
        target.y -= envelope * (gesture.kind === 'hello' ? 2.8 : 5);
        if (progress >= 1) gesture = null;
      }
      const omega = 13;
      const decay = Math.exp(-omega * dt);
      let moving = Boolean(gesture);
      keys.forEach(key => {
        const offset = position[key] - target[key];
        const correction = velocity[key] + omega * offset;
        position[key] = target[key] + (offset + correction * dt) * decay;
        velocity[key] = (velocity[key] - omega * correction * dt) * decay;
        if (Math.abs(position[key] - target[key]) > 0.003 || Math.abs(velocity[key]) > 0.015) moving = true;
      });
      render();
      if (moving) frame = requestAnimationFrame(tick);
      else settle();
    };
    const move = () => {
      if (!allowed()) { settle(); return; }
      root.classList.add('is-playing');
      root.dataset.motion = gesture ? gesture.kind : steps[step];
      if (!frame) { lastTime = 0; frame = requestAnimationFrame(tick); }
    };
    const controls = () => {
      hello.hidden = !ready;
      if (hint) hint.hidden = !ready;
      pause.hidden = !ready;
      pause.disabled = reduced.matches;
      pause.textContent = reduced.matches ? pause.dataset.reduced : paused ? pause.dataset.resume : pause.dataset.pause;
      pause.setAttribute('aria-pressed', String(paused || reduced.matches));
      celebrate.hidden = !ready || step !== 2;
    };
    const reflectStep = (animate = true) => {
      const next = tabs.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
      if (next < 0 || next >= steps.length) return;
      if (root.dataset.step === steps[next]) return;
      step = next;
      root.dataset.step = steps[step];
      caption.textContent = root.dataset[steps[step]];
      gesture = null;
      controls();
      art?.update(step, animate);
      if (animate) move(); else settle();
    };
    const greet = event => {
      if (event.type === 'pointerenter' && (!finePointer.matches || event.pointerType === 'touch')) return;
      caption.textContent = root.dataset.hello;
      if (gesture && gesture.kind === 'hello') return;
      gesture = { kind: 'hello', duration: 1100, start: null };
      move();
    };
    listen(image, 'load', () => { const wasReady = ready; ready = image.naturalWidth > 0; controls(); if (!wasReady) settle(); });
    listen(image, 'error', () => { ready = false; art?.recover(); settle(); controls(); });
    listen(hello, 'click', greet);
    listen(hello, 'pointerenter', greet);
    listen(hello, 'focus', greet);
    listen(celebrate, 'click', () => {
      caption.textContent = root.dataset.learn;
      gesture = { kind: 'celebrate', duration: 1200, start: null };
      move();
    });
    listen(pause, 'click', () => {
      paused = !paused;
      try { sessionStorage.setItem('di-pex-paused', paused ? '1' : '0'); } catch {}
      document.dispatchEvent(new CustomEvent('di:pex-motion', { detail: { paused } }));
      settle();
      controls();
    });
    listen(document, 'di:pex-motion', event => {
      paused = Boolean(event.detail && event.detail.paused);
      settle();
      controls();
    });
    listen(reduced, 'change', () => { settle(); controls(); });
    listen(document, 'visibilitychange', () => { if (document.hidden) settle(); });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        visible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio > 0);
        if (!visible) settle();
      }, { threshold: [0, 0.1] });
      observer.observe(root);
      cleanup.push(() => observer.disconnect());
    }
    if (tabRoot) {
      const observer = new MutationObserver(() => reflectStep());
      observer.observe(tabRoot, { subtree: true, attributes: true, attributeFilter: ['aria-selected'] });
      cleanup.push(() => observer.disconnect());
    }
    reflectStep(false);
    controls();
    render();
    instances.set(root, () => {
      destroyed = true;
      settle();
      cleanup.forEach(fn => fn());
      instances.delete(root);
    });
  }
  const scan = scope => {
    if (scope.matches && scope.matches('[data-di-pex]')) init(scope);
    scope.querySelectorAll('[data-di-pex]').forEach(init);
  };
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((dispose, root) => { if (event.target.contains(root)) dispose(); });
  });
})();
